import { space } from '@primer-io/goat'
import Clickable from 'components/Clickable'
import CodeBlock from 'components/CodeBlock'
import { withSideAnchor } from 'components/SideAnchor'
import { TabsWrapper } from 'components/Tabs'
import { Root as CalloutRoot } from 'ds/Callout'
import { ContentGridRoot } from 'ds/ContentGrid'
import { Root as DefinitionListRoot } from 'ds/DefinitionList'
import * as Typography from 'ds/Text'
import * as TextGoat from 'ds/TextGoat'
import { uniqueId } from 'lodash'
import React, { createContext, useContext, useMemo } from 'react'
import slugify from 'slugify'
import styled from 'styled-components'
import theme from '../../theme'

export { default as Callout } from 'ds/Callout'

const h1 = styled(TextGoat.H1)``

const h2 = styled(TextGoat.H2)`
	margin-top: 40px;

	${p => p.theme.above('medium')`
		margin-top: 56px;
	`}
`
const h3 = styled(TextGoat.H3)`
	margin-top: 24px;

	${p => p.theme.above('medium')`
		margin-top: 32px;
	`}
`
const h4 = styled(TextGoat.H4)`
	margin-top: 16px;
`
const h5 = styled(TextGoat.H5)`
	margin-top: 16px;
`
const h6 = styled(TextGoat.H6)`
	margin-top: 16px;
`

const headers = [h1, h2, h3, h4, h5, h6]

const HeaderOffsetContext = createContext()

export const HeaderOffsetProvider = ({ children, topLevel }) => (
	<HeaderOffsetContext.Provider value={{ topLevel }}>{children}</HeaderOffsetContext.Provider>
)

const withHeaderOffset = defaultHeaderLevel => props => {
	const context = useContext(HeaderOffsetContext)

	const Component = headers[defaultHeaderLevel - 1 + (context?.topLevel ?? 1) - 1] ?? h6

	const withHeaderOffsetComponent = <Component {...props} />
	return withHeaderOffsetComponent
}

const withAutolink =
	Component =>
	({ id, children, ...otherProps }) => {
		const newId = useMemo(() => {
			if (id) {
				return id
			}

			if (children && typeof children === 'string') {
				return slugify(children, { lower: true, strict: true })
			}

			return uniqueId()
		}, [])

		const withAutoLinkComponent = (
			<Component id={newId} {...otherProps}>
				{children}
			</Component>
		)

		return withAutoLinkComponent
	}

const withLink = defaultHeaderLevel => withAutolink(withSideAnchor(withHeaderOffset(defaultHeaderLevel)))

/* eslint-disable react/prop-types */
export const H1 = props => withLink(1)(props)
export const H2 = props => withLink(2)(props)
export const H3 = props => withLink(3)(props)
export const H4 = props => withLink(4)(props)
export const H5 = props => withLink(5)(props)
export const H6 = props => withLink(6)(props)

export const P = styled(TextGoat.BodyLarge)`
	& + & {
		margin-top: 8px;
	}
	h2 + & {
		margin-top: 16px;
	}

	h3 + &,
	h4 + &,
	h5 + &,
	h6 + & {
		margin-top: 8px;
	}
`

export const Code = styled(Typography.Code)`
	color: var(--color-red);
`

export const A = styled(Clickable).attrs(({ href, ...props }) => ({ ...props, to: href }))`
	color: var(--color-blue);

	&:hover {
		text-decoration: underline;
	}
`

export const StyledCodeBlock = styled(CodeBlock)`
	margin: 32px 0;

	& + .gatsby-resp-image-wrapper,
	& + table,
	& + ol,
	& + ul,
	& + .block_like,
	& + ${ContentGridRoot}, & + ${TabsWrapper}, & + &,
	& + ${CalloutRoot}, & + blockquote {
		margin-top: 0px !important;
	}

	.gatsby-resp-image-wrapper + &,
	table + &,
	ol + &,
	ul + &,
	.block_like + &,
	${ContentGridRoot} + &,
	${TabsWrapper} + &,
	${CalloutRoot} + &,
	blockquote + & {
		margin-top: 0px !important;
	}
`

export const CheckIcon = ({ className }) => (
	<svg xmlns='http://www.w3.org/2000/svg' width={20} height={20} viewBox='0 0 20 20' className={className}>
		<g fill='none' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round'>
			<path d='M7.125 9.70817L9.5 12.0832l7.9167-7.9167' />
			<path d='M16.625 10.5v5.5417c0 .4199-.1668.8226-.4637 1.1196-.297.2969-.6997.4637-1.1196.4637H3.95833c-.41992 0-.82265-.1668-1.11958-.4637-.29694-.297-.46375-.6997-.46375-1.1196V4.95833c0-.41992.16681-.82265.46375-1.11958.29693-.29694.69966-.46375 1.11958-.46375h8.70837' />
		</g>
	</svg>
)

export const Check = styled(CheckIcon)`
	vertical-align: middle;
	width: auto;
	height: 1em;
`

export const HorizontalScroll = styled.div`
	width: 100%;
	overflow-x: auto;
	position: relative;
`

export const Table = styled(TextGoat.BodyLarge).attrs({ as: 'table' })`
	margin: 24px 0;
	min-width: 100%;

	th {
		background: var(--color-background);
		${theme.typography.scale.caption2.regular}
		padding: 10px 16px;
		text-align: left;
		vertical-align: baseline;

		strong {
			font-weight: inherit !important;
		}
	}

	tr:not(:last-of-type) {
		border-bottom: 1px var(--color-lightGrey) solid;
	}

	td {
		padding: 16px;
		${theme.typography.scale.body1.regular}
		vertical-align: baseline;
	}
`

export const HorizontalScrollTable = p => (
	<HorizontalScroll>
		<Table {...p} />
	</HorizontalScroll>
)

export const Blockquote = styled.blockquote`
	border-left: 2px solid var(--color-lightGrey);
	padding-left: 32px;
	margin: 32px 0px;

	p {
		${theme.typography.scale.body1.regular}
		padding-bottom: 16px;
	}

	.gatsby-resp-image-wrapper + &,
	table + &,
	ol + &,
	ul + &,
	.block_like + &,
	${ContentGridRoot} + &,
	${TabsWrapper} + &,
	${CalloutRoot} + & {
		margin-top: 0px !important;
	}

	& + .gatsby-resp-image-wrapper,
	& + table,
	& + ol,
	& + ul,
	& + .block_like,
	& + ${ContentGridRoot}, & + ${TabsWrapper}, & + ${CalloutRoot} {
		margin-top: 0px !important;
	}
`

export const HR = styled.hr`
	display: block;
	height: 1px;
	background: var(--color-lighterGrey);
	border: none;
	margin: 56px 0px;
`

const Ol = styled.ol`
	display: flex;
	counter-reset: custom-counter;
	gap: ${space(6)};
	flex-direction: column;
	margin: 0;
	margin-top: ${space(6)};
	margin-bottom: ${space(8)};
`

const OrderedListNumber = styled.div`
	${theme.typography.scale.caption2.regular}
	background: var(--color-background);
	border-radius: 50%;
	color: var(--color-text);
	content: counter(custom-counter);
	display: flex;
	flex-shrink: 0;
	height: 24px;
	left: 0;
	line-height: 24px;
	place-content: center;
	place-items: center;
	position: absolute;
	width: 24px;
`

const OrderedListItem = styled(TextGoat.BodyLarge).attrs({ as: 'li' })`
	counter-increment: custom-counter;
	display: block;
	padding-left: ${space(10)};
	position: relative;
	width: 100%;

	ul {
		margin: 24px 0 0 0;
	}
`

export const OrderedList = ({ children, start }) => {
	return (
		<Ol>
			{children.filter?.(Boolean).map((child, i) => {
				// `child` is already li
				// We want to recreate the li to have moar control
				// Hence why we need to extract the children of the li to use them separately
				const childrenOfLi = child.props.children

				return (
					<OrderedListItem key={i}>
						<OrderedListNumber>{(start ?? 0) + i + 1}</OrderedListNumber>
						{childrenOfLi}
					</OrderedListItem>
				)
			})}
		</Ol>
	)
}

const UnorderListWrapper = styled.ul`
	& {
		margin: 24px 0px 32px 32px;
		list-style-type: disc;
	}

	p + & {
		margin-top: 12px;
	}

	& li {
		width: 100%;

		& + li {
			padding-top: 12px;
		}
	}
`

const UnorderedListItem = styled(TextGoat.BodyLarge).attrs(p => ({ as: 'li', ...p }))``

export const UnorderedList = ({ children }) => {
	return (
		<UnorderListWrapper>
			{Array.isArray(children) ? (
				children.filter?.(Boolean).map((child, i) => {
					// `child` is already li
					// We want to recreate the li to have moar control
					// Hence why we need to extract the children of the li to use them separately
					const childrenOfLi = child.props.children

					return <UnorderedListItem key={i}>{childrenOfLi}</UnorderedListItem>
				})
			) : (
				<UnorderedListItem key={0}>{children.props.children}</UnorderedListItem>
			)}
		</UnorderListWrapper>
	)
}

export const SmallSeparator = styled.hr`
	display: block;
	height: 1px;
	background: var(--color-lighterGrey);
	border: none;
	margin: 8px 0px;
`

export const Footnote = styled(Typography.Caption1)`
	margin-top: -32px;
	display: block;
	${p => p.right && `text-align: right;`}
	${p => p.center && `text-align: center;`}
`

export const Headnote = styled(Typography.Caption1)`
	margin-bottom: -32px;
	display: block;
	${p => p.right && `text-align: right;`}
	${p => p.center && `text-align: center;`}
`

export const Wrapper = styled.article`
	code {
		color: var(--color-red);
		${theme.typography.scale.code.regular}
		line-height: inherit !important;
	}

	* {
		scroll-margin-top: 5rem;
	}

	strong,
	b {
		font-weight: 500;
	}
	em,
	i {
		font-style: italic;
	}

	.gatsby-resp-image-wrapper,
	.gria-image-wrapper {
		// 8px top because the this wrapper is inside paragraph which has 16px top spacing already
		margin: 8px 0px;
	}

	.gatsby-resp-image-image,
	.gria-image {
		// We put some max-height to Gatsby
		object-fit: contain !important;
	}

	.gria-image-placeholder {
		background-size: contain !important;
	}

	p:first-child {
		.gatsby-resp-image-wrapper {
			margin-top: 0px;
		}
	}

	// Block resets
	.gatsby-resp-image-wrapper {
		& + table,
		& + ${Ol}, & + ul,
		& + &,
		& + .block_like,
		& + ${ContentGridRoot}, & + ${CalloutRoot}, & + ${TabsWrapper}, & + blockquote {
			margin-top: 0px !important;
		}
	}

	table {
		& + .gatsby-resp-image-wrapper,
		& + ol,
		& + ul,
		& + &,
		& + .block_like,
		& + ${ContentGridRoot}, & + ${CalloutRoot}, & + ${TabsWrapper}, & + blockquote {
			margin-top: 0px !important;
		}
	}

	${Ol} {
		& + .gatsby-resp-image-wrapper,
		& + table,
		& + ul,
		& + &,
		& + .block_like,
		& + ${ContentGridRoot}, & + ${CalloutRoot}, & + ${TabsWrapper}, & + blockquote {
			margin-top: 0px !important;
		}
	}

	ul {
		& + .gatsby-resp-image-wrapper,
		& + table,
		& + ${Ol}, & + &,
		& + .block_like,
		& + ${ContentGridRoot}, & + ${CalloutRoot}, & + ${TabsWrapper}, & + blockquote {
			margin-top: 0px !important;
		}
	}

	${CalloutRoot}, ${TabsWrapper}, ${ContentGridRoot}, .block_like {
		margin: 32px 0px;
	}

	${ContentGridRoot} ${CalloutRoot} {
		margin: 0px;
	}

	${CalloutRoot} {
		*:first-child {
			margin-top: 0px !important;
		}

		*:last-child {
			margin-bottom: 0px !important;
		}
	}

	p a {
		display: inline !important;
	}

	& > p:first-child {
		margin-top: 16px;
	}

	& > section:first-child > * {
		margin-top: 0 !important;
	}

	& > h2:first-child,
	& > h3:first-child,
	& > h4:first-child,
	& > p:first-child {
		padding-top: 0px !important;
	}

	& > *:last-child {
		margin-bottom: 0px !important;
	}

	.gatsby-resp-image-figcaption {
		font-style: italic;
		text-align: center;
		padding-top: 4px;
	}

	ul:first-child {
		margin-top: 0px;
	}

	${DefinitionListRoot} ul {
		margin-left: 8px;
	}
`
